import React, {createContext, ReactNode, useCallback, useState} from 'react';
import {equals} from 'ramda';
import {Toast, ToastProps, ToastType} from '@/ui-kits/Toast';
import {cn} from '@/utils/common_functions';

// types
type Toast = {
  readonly id: string;
} & Omit<ToastProps, 'forceClose'>;

export interface ToastContextType {
  addToast: (id: string, title: string, message: string, type: ToastType, duration: number) => void;
  removeToast: (id: string) => void;
}

// creating context with default value
const ToastContext = createContext<ToastContextType>({
  addToast: () => {},
  removeToast: () => {},
});
const Provider = ToastContext.Provider;

// Toast Provider
export const ToastProvider: React.FC<{children: ReactNode}> = ({children}) => {
  // stack of toast such that we can have multiple toasts
  const [toasts, setToasts] = useState<Toast[]>([]);

  // function for displaying toasts
  const addToast = useCallback((id: string, title: string, message: string, type: ToastType, duration: number) => {
    const newToast = {id: id, title, message, type, duration};
    setToasts(prev => {
      // search for existing id
      for (let i of prev) {
        if (equals(i, newToast)) {
          return prev;
        }
      }
      return [newToast];
    });
  }, []);

  // function for removing toasts
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <Provider value={{addToast, removeToast}}>
      {/* render the app */}
      {children}
      <div className={cn('fixed top-0 right-10 flex flex-col gap-1 z-[10] items-end', toasts.length > 0 && 'p-4')}>
        {toasts.map(item => (
          <Toast key={item.id} {...item} forceClose={() => removeToast(item.id)} />
        ))}
      </div>
      {/* components to display all the toast as an overlay */}
    </Provider>
  );
};

export default ToastContext;
